import { useEffect, useRef, useState } from "react";
import { Form, Input, Select, Button, Typography, Row, Col } from "antd";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { stateOptions } from "@/config";
import useGlobalStore from '@/store/global'

const { Text } = Typography;
const { Option } = Select;

const ProviderEnrollmentForm = () => {
  const { themeColor, updateMeData } = useGlobalStore();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const loadData = async () => {
    const { data } = await axios.get("sessions/me");
    form.setFieldsValue(data);
    updateMeData(data);
  };

  let renderRef = useRef(false);

  useEffect(() => {
    if (renderRef.current) {
      renderRef.current = false;
      return;
    }
    loadData();
  }, []);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await axios.post("sessions/upgrade", values);
      await loadData();
      navigate("/");
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl md:text-2xl font-bold" style={{ color: themeColor }}>
        Edit Provider Profile
      </h2>
      <hr className="my-2" />
      <Text style={{ fontWeight: 500 }}>
        Providers make changes to your profile below and press "Submit".
      </Text>

      <div>
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Row gutter={20}>
            <Col xs={24} md={12}>
              <Form.Item
                name="business_name"
                label="Business Name"
                className="mt-4"
                rules={[{ required: true }]}
              >
                <Input placeholder="Financial reimbursement will be issued to this name" />
              </Form.Item>
            </Col>
          </Row>

          <div className="py-5">
            <h3 className="text-lg md:text-xl font-bold">SECTION 1: PHARMACY LOCATION</h3>
            <Text>
              THIS SECTION IS MANDATORY. IT REQUIRES THE ADDRESS AND SPECIFIC
              INFORMATION OF YOUR PHARMACY.
            </Text>
          </div>

          <Row gutter={20}>
            <Col xs={24} md={12}>
              <Form.Item
                name="setting"
                label="Location Setting"
                rules={[{ required: true }]}
              >
                <Select placeholder="Setting of this pharmacy location">
                  <Option value={1} key="1">
                    Physician Infusion Center
                  </Option>
                  <Option value={2} key="2">
                    Hospital Outpatient Facility
                  </Option>
                  <Option value={4} key="4">
                    Specialty Pharmacy (Home Infusion)
                  </Option>
                  <Option value={8} key="8">
                    Specialty Pharmacy
                  </Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="business_address"
                label="Location Address"
                rules={[{ required: true }]}
              >
                <Input placeholder="Location street address" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="business_address_line2"
                label="Location address Line 2"
              >
                <Input placeholder="Location street address line 2" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="business_city"
                label="Location city"
                rules={[{ required: true }]}
              >
                <Input placeholder="Location city" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="business_state"
                label="Location State"
                rules={[{ required: true }]}
              >
                <Select placeholder="Location state">
                  {stateOptions.map((option) => (
                    <Option value={option.value} key={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name="business_zip" label="Location ZIP">
                <Input placeholder="Location ZIP code (5-digits e.g. 91101)" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="business_phone"
                label="Location phone"
                rules={[{ required: true }]}
              >
                <Input placeholder="Location phone (including area code)" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="business_fax"
                label="Location fax"
                rules={[{ required: true }]}
              >
                <Input placeholder="Location fax number" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="point_of_contact"
                label="Location contact person name"
                rules={[{ required: true }]}
              >
                <Input placeholder="First and last name of the billing contact at this location" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="tax_id"
                label="Location tax ID"
                rules={[{ required: true }]}
              >
                <Input placeholder="Please provide the tax ID for this location" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="provider_npi"
                label="Provider NPI"
                rules={[{ required: true }]}
              >
                <Input placeholder="Please provide the 10-digit NPI number for this location" />
              </Form.Item>
            </Col>
          </Row>

          <div className="py-5">
            <h3 className="text-lg md:text-xl font-bold">
              SECTION 2: ALTERNATE FINANCIAL REIMBURSEMENT ADDRESS
            </h3>
            <Text>
              THIS SECTION IS OPTIONAL. IT IS ONLY REQUIRED IF YOUR PHARMACY
              LOCATION (ADDRESS LISTED ABOVE) WANTS TO RE-DIRECT COPAY
              ASSISTANCE REIMBURSEMENT TO A DIFFERENT ADDRESS. IF LEFT BLANK,
              REIMBURSEMENT WILL BE SENT TO THE ADDRESS LISTED IN SECTION 1
              ABOVE.
            </Text>
          </div>

          <Row gutter={20}>
            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address"
                label="Financial reimbursement address"
              >
                <Input placeholder="Street address where financial reimbursement would be sent" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address_line2"
                label="Financial reimbursement address line 2"
              >
                <Input placeholder="Financial reimbursement address line 2" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address_city"
                label="Financial reimbursement city"
              >
                <Input placeholder="Financial reimbursement city" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address_state"
                label="Financial reimbursement state"
              >
                <Select placeholder="Financial reimbursement state">
                  {stateOptions.map((option) => (
                    <Option value={option.value} key={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_address_zip"
                label="Financial reimbursement ZIP"
              >
                <Input placeholder="Financial reimbursement ZIP code (5-digits e.g. 91101)" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_contact_name"
                label="Financial reimbursement contact person name"
              >
                <Input placeholder="First and last name of the financial reimbursement contact" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="reimbursement_contact_phone"
                label="Financial reimbursement contact person phone"
              >
                <Input placeholder="Financial reimbursement contact direct phone (including area code)" />
              </Form.Item>
            </Col>
          </Row>

          <div className="py-5">
            <h3 className="text-lg md:text-xl font-bold">
              SECTION 3: CREATE YOUR ACCOUNT
            </h3>
            <Text>
              THIS SECTION IS MANDATORY, THE E-MAIL ADDRESS AND THE PASSWORD YOU CREATE HERE WILL ENABLE YOU TO LOG INTO THIS PORTAL.
            </Text>
          </div>

          <Row gutter={20}>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Your email"
                rules={[{ required: true, type: "email" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="password"
                label="Current password"
                rules={[{ required: true, min: 8 }]}
                extra="Please verify your current password"
              >
                <Input.Password />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item className="mt-4 md:w-1/2 mx-auto">
            <Button
              type="primary"
              htmlType="submit"
              disabled={loading}
              loading={loading}
              style={{
                width: "100%",
                backgroundColor: themeColor,
                height: "40px",
                fontSize: "18px",
              }}
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default ProviderEnrollmentForm;
